import { Component, ElementRef, HostListener, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core'
import { DOCUMENT } from '@angular/common'
import { ActivatedRoute, Router } from '@angular/router'
import { forkJoin, of } from 'rxjs'
import { catchError } from 'rxjs/operators'

import { AuthService } from '@data/services/modules/auth/auth.service'
import { UserRole } from '@data/enums/user-role.enum'
import { ProjectWithStatus } from '@modules/private/worklog/data/interfaces/project.interface'
import { WorklogService, worklogErrorMessage } from '@modules/private/worklog/data/services/worklog.service'
import { buildShowcaseDoc } from '../../data/constants/showcase-shell.constant'
import { EPIC_CATEGORIES, PROJECT_STATUS_META } from '../../data/constants/roadmap.constants'

/**
 * FICHA VISUAL de un proyecto: el HTML que explica de un vistazo qué le suma a la plataforma.
 *
 * ## Por qué un iframe y no `[innerHTML]`
 *
 * `worklog-rich-text.component.ts` deja escrita la postura del repo: nada de HTML almacenado
 * pintado con `innerHTML`, porque un fallo del sanitizador se vuelve XSS **con persistencia**. Esa
 * decisión no se revierte aquí, se rodea: no hay `innerHTML` ni sanitizador de por medio.
 *
 * El documento se pinta como `srcdoc` de un iframe con `sandbox` **sin `allow-scripts` ni
 * `allow-same-origin`**, así que el navegador le da un **origen opaco**: `<script>`, `onerror` y
 * `onload` quedan inertes, y aunque algo corriera no alcanza el DOM del panel, ni las cookies, ni
 * el `localStorage` donde vive la sesión. El aislamiento sustituye al sanitizador.
 *
 * Importa más de lo que parece: **el panel no tiene CSP en ninguna capa** —`nginx.conf` no pone un
 * solo `add_header`—, así que la defensa del propio render es la única que hay.
 *
 * ## Por qué el iframe se crea a mano y no con `[srcdoc]` en la plantilla
 *
 * Con el binding de Angular **el frame nunca navega**: el atributo queda puesto en el DOM con el
 * documento entero y aun así el contenido no se parsea — verificado en Chrome, sin una sola
 * petición de red desde dentro del frame ni después de 12 segundos. Un iframe al que se le pone
 * `srcdoc` DESPUÉS de estar insertado se queda con el `about:blank` que cargó al nacer.
 *
 * Creándolo aquí, el `srcdoc` va puesto **antes** de insertarlo y el documento carga a la primera.
 * De paso desaparece el `bypassSecurityTrustHtml`: como el atributo se escribe sobre un elemento
 * propio, el sanitizador de Angular no entra en la ecuación y no hay ningún bypass que auditar.
 *
 * El estilo del iframe va inline a propósito: al no venir de la plantilla no lleva el atributo de
 * encapsulación, así que el CSS del componente no lo alcanzaría — y acotarlo con `::ng-deep` está
 * desaconsejado en este repo.
 *
 * ## Por qué el estilo no viene en el HTML guardado
 *
 * El campo trae solo el cuerpo; el `<head>` con la marca lo pone `buildShowcaseDoc()`. Con decenas
 * de fichas, el estilo dentro de cada una obligaría a regenerarlas todas para cambiar la marca — y
 * dejaría que cada proyecto se viera distinto, que es justo lo contrario de lo que se busca.
 */
@Component({
  selector: 'app-roadmap-project-showcase',
  templateUrl: './roadmap-project-showcase.component.html',
  styleUrls: ['./roadmap-project-showcase.component.scss'],
})
export class RoadmapProjectShowcaseComponent implements OnInit, OnDestroy {
  readonly PROJECT_STATUS_META = PROJECT_STATUS_META

  slug = ''
  row: ProjectWithStatus | null = null
  loading = true
  errorMsg = ''

  //* Se guarda en crudo porque el iframe y el botón de descargar arman el mismo archivo, y volver
  //* a pedirlo sería una segunda llamada para un dato que ya está en memoria.
  private rawDoc = ''

  /** `true` mientras el navegador está en pantalla completa de verdad (no solo la capa). */
  enPantallaCompleta = false

  //* El lienzo entero es lo que se manda a pantalla completa, no el iframe: así la barra con
  //* Salir sigue a la vista. Un documento sin salida visible es una trampa.
  @ViewChild('lienzo') lienzo?: ElementRef<HTMLElement>

  /** `true` cuando hay ficha que pintar. Gobierna el `*ngIf` del contenedor del iframe. */
  get hayFicha(): boolean {
    return !!this.rawDoc
  }

  //* Setter y no propiedad: el contenedor vive tras un `*ngIf`, así que aparece DESPUÉS de que
  //* llegan los datos. Esto se dispara justo cuando existe, que es cuando se puede montar.
  @ViewChild('marco') set marco(ref: ElementRef<HTMLElement> | undefined) {
    if (ref && this.rawDoc) this.montarFicha(ref.nativeElement)
  }

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    @Inject(DOCUMENT) private readonly documento: Document,
    private readonly auth: AuthService,
    private readonly worklog: WorklogService
  ) {}

  ngOnInit(): void {
    //! Mismo criterio que la pestaña Proyectos: la ficha lleva el costo de desarrollar el proyecto
    //! por fuera. El backend igual responde 403, pero mandar aquí a quien no puede verla solo
    //! produce una pantalla de error donde debería haber una redirección.
    if (this.auth.getUser?.user?.permissions?.name !== UserRole.ADMIN) {
      this.router.navigate(['/roadmap'])
      return
    }

    //* La ficha se abre como capa sobre todo el panel. Sin esto, la página de atrás sigue
    //* desplazándose con la rueda aunque no se vea, y al salir se vuelve a otra posición.
    this.documento.body.style.overflow = 'hidden'
    this.documento.addEventListener('fullscreenchange', this.alCambiarPantalla)

    this.slug = this.route.snapshot.paramMap.get('slug') ?? ''
    if (!this.slug) {
      this.router.navigate(['/roadmap'])
      return
    }
    this.load()
  }

  ngOnDestroy(): void {
    this.documento.body.style.overflow = ''
    this.documento.removeEventListener('fullscreenchange', this.alCambiarPantalla)
    //* Salir de la ruta con el navegador todavía en pantalla completa dejaría el panel entero
    //* a pantalla completa sin que nadie lo haya pedido.
    if (this.documento.fullscreenElement) void this.documento.exitFullscreen().catch(() => undefined)
  }

  //* Función-propiedad para poder quitar el listener con la MISMA referencia en ngOnDestroy.
  private readonly alCambiarPantalla = (): void => {
    this.enPantallaCompleta = !!this.documento.fullscreenElement
  }

  /**
   * Esc cierra la ficha. Con el navegador en pantalla completa **no**: ahí Esc ya significa
   * "sal de pantalla completa", y encadenar las dos cosas sacaría al usuario de la ficha cuando
   * solo quería volver a la ventana.
   */
  @HostListener('document:keydown.escape')
  alPresionarEsc(): void {
    if (this.documento.fullscreenElement) return
    this.volver()
  }

  /** Pantalla completa del navegador, para presentar la ficha en una pantalla o proyector. */
  async alternarPantallaCompleta(): Promise<void> {
    try {
      if (this.documento.fullscreenElement) await this.documento.exitFullscreen()
      else if (this.lienzo) await this.lienzo.nativeElement.requestFullscreen()
    } catch {
      //* Si el navegador la niega (permiso, iframe embebido), la capa sigue ocupando la pantalla:
      //* no hay nada que reportar al usuario porque no pierde funcionalidad.
    }
  }

  private load(): void {
    this.loading = true
    this.errorMsg = ''
    //* La cabecera (nombre, estado, avance) sale del listado, que ya está cacheado del lado del
    //* backend; el cuerpo va por su endpoint. En paralelo para no encadenar dos esperas.
    forkJoin({
      proyectos: this.worklog.getProjects(),
      ficha: this.worklog.getShowcase(this.slug).pipe(
        catchError((err) => {
          this.errorMsg = worklogErrorMessage(err, 'No se pudo cargar la ficha del proyecto.')
          return of({ body: null, updatedAt: null })
        })
      ),
    }).subscribe(({ proyectos, ficha }) => {
      this.row = proyectos.find((p) => p.project.slug === this.slug) ?? null
      if (ficha.body) this.rawDoc = buildShowcaseDoc(ficha.body)
      this.loading = false
    })
  }

  /**
   * Monta el iframe con el documento ya dentro. El orden es lo único que importa aquí: `srcdoc`
   * ANTES de `appendChild`. Al revés se queda en blanco — ver el docblock de la clase.
   */
  private montarFicha(marco: HTMLElement): void {
    marco.replaceChildren()
    const frame = this.documento.createElement('iframe')
    //! Sin `allow-scripts` ni `allow-same-origin`: origen opaco. Es TODA la defensa del panel
    //! contra el HTML que llega de la base, así que no se relaja para "arreglar" nada.
    frame.setAttribute('sandbox', 'allow-popups allow-popups-to-escape-sandbox')
    frame.setAttribute('title', `Ficha del proyecto ${this.row?.project.name ?? ''}`)
    frame.setAttribute('style', 'display:block;width:100%;height:100%;border:0;border-radius:13px')
    frame.setAttribute('srcdoc', this.rawDoc)
    marco.appendChild(frame)
  }

  get category() {
    return EPIC_CATEGORIES.find((c) => c.id === this.row?.project.category) ?? null
  }

  get accent(): string {
    return this.category?.color ?? '#6366f1'
  }

  get progress(): number {
    const r = this.row
    if (!r?.taskCount) return 0
    return Math.round((r.doneCount / r.taskCount) * 100)
  }

  volver(): void {
    this.router.navigate(['/roadmap'])
  }

  /**
   * Baja la ficha como un `.html` que se abre solo en cualquier navegador — es lo que permite
   * mandársela a alguien sin darle acceso al panel. Se arma el MISMO documento que se está viendo,
   * shell incluido, o el archivo saldría sin estilo.
   */
  descargar(): void {
    if (!this.rawDoc) return
    const url = URL.createObjectURL(new Blob([this.rawDoc], { type: 'text/html;charset=utf-8' }))
    const a = document.createElement('a')
    a.href = url
    a.download = `${this.slug}-ficha.html`
    a.click()
    URL.revokeObjectURL(url)
  }
}
